import * as THREE from 'three';
import { liftSparkTint, sampleCloudTint } from './sampleCloudTint';

export const IGNITION_DURATION_MS = 6200;
export const IGNITION_DELAY_MS = 120;
export const IGNITION_TRAVEL_MS = 1350;

const SEGS = 96;
const RING_MS = 1460;
const WHITE = new THREE.Color('#eef2ff');
const VOID = new THREE.Color('#c5d0ee');
const _paint = new THREE.Color();
const _tint = new THREE.Color();
const _fallback = new THREE.Color();
const _p = new THREE.Vector3();
const _tan = new THREE.Vector3();
const _side = new THREE.Vector3();
const HEAD_PLANE = new THREE.PlaneGeometry(2, 2);
const CORE_TORUS = new THREE.TorusGeometry(1, 0.011, 16, 192);
const SHEATH_TORUS = new THREE.TorusGeometry(1, 0.036, 12, 128);

const BEAM_VERT = /* glsl */ `
  attribute float aAlong;
  attribute float aSide;
  attribute vec3 aColor;
  varying float vAlong;
  varying float vSide;
  varying vec3 vColor;
  void main() {
    vAlong = aAlong;
    vSide = aSide;
    vColor = aColor;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const BEAM_CORE_FRAG = /* glsl */ `
  uniform float uHead;
  uniform float uFade;
  varying float vAlong;
  varying float vSide;
  varying vec3 vColor;
  void main() {
    float lit = 1.0 - smoothstep(uHead, uHead + 0.01, vAlong);
    float wake = exp(-max(uHead - vAlong, 0.0) * 4.4);
    float tip = exp(-(vAlong - uHead) * (vAlong - uHead) * 140.0);
    float along = max(tip, wake * 0.7) * lit * uFade;
    if (along < 0.006) discard;
    float x = abs(vSide);
    float core = exp(-x * x * 220.0);
    float rim = exp(-x * x * 28.0) * 0.2;
    vec3 col = mix(vColor, vec3(1.0), 0.22) * (core * 6.2 + rim * 1.6);
    gl_FragColor = vec4(col, (core + rim) * along);
  }
`;
const BEAM_SHEATH_FRAG = /* glsl */ `
  uniform float uHead;
  uniform float uFade;
  varying float vAlong;
  varying float vSide;
  varying vec3 vColor;
  void main() {
    float lit = 1.0 - smoothstep(uHead, uHead + 0.04, vAlong);
    float wake = exp(-max(uHead - vAlong, 0.0) * 1.7);
    float tip = exp(-(vAlong - uHead) * (vAlong - uHead) * 36.0);
    float along = max(tip, wake * 0.9) * lit * uFade;
    if (along < 0.01) discard;
    float x = abs(vSide);
    float volume = exp(-x * x * 4.6);
    vec3 col = vColor * volume * 1.35;
    gl_FragColor = vec4(col, volume * along * 0.32);
  }
`;

const HEAD_CORE_FRAG = /* glsl */ `
  uniform float uOpacity;
  uniform vec3 uColor;
  varying vec2 vUv;
  void main() {
    vec2 p = vUv * 2.0 - 1.0;
    float r2 = dot(p, p);
    if (r2 > 1.0) discard;
    float core = exp(-r2 * 26.0);
    float mid = exp(-r2 * 7.5);
    vec3 col = mix(uColor, vec3(1.0), 0.48) * (core * 8.4 + mid * 1.55);
    gl_FragColor = vec4(col, (core + mid * 0.28) * uOpacity);
  }
`;
const HEAD_HALO_FRAG = /* glsl */ `
  uniform float uOpacity;
  uniform vec3 uColor;
  varying vec2 vUv;
  void main() {
    vec2 p = vUv * 2.0 - 1.0;
    float r2 = dot(p, p);
    if (r2 > 1.0) discard;
    float haze = exp(-r2 * 3.4);
    vec3 col = uColor * haze * 1.15;
    gl_FragColor = vec4(col, haze * uOpacity * 0.28);
  }
`;

const RING_VERT = /* glsl */ `
  uniform float uRadius;
  uniform float uTube;
  varying vec3 vWorld;
  varying vec3 vNormalW;
  void main() {
    vec2 dir = normalize(position.xy + vec2(1e-6, 0.0));
    vec3 major = vec3(dir, 0.0);
    vec3 tube = position - major;
    vec3 local = major * uRadius + tube * uTube;
    vec4 world = modelMatrix * vec4(local, 1.0);
    vWorld = world.xyz;
    vNormalW = normalize(mat3(modelMatrix) * normalize(tube + vec3(0.0, 0.0, 0.0001)));
    gl_Position = projectionMatrix * viewMatrix * world;
  }
`;
const RING_CORE_FRAG = /* glsl */ `
  uniform vec3 uColor;
  uniform float uOpacity;
  varying vec3 vWorld;
  varying vec3 vNormalW;
  void main() {
    vec3 view = normalize(cameraPosition - vWorld);
    float ndv = abs(dot(normalize(vNormalW), view));
    float fres = pow(clamp(1.0 - ndv, 0.0, 1.0), 2.1);
    float body = 0.3 + fres * 0.75;
    vec3 col = mix(uColor, vec3(1.0), fres * 0.18) * (1.7 + fres * 1.4);
    gl_FragColor = vec4(col, body * uOpacity);
  }
`;
const RING_SHEATH_FRAG = /* glsl */ `
  uniform vec3 uColor;
  uniform float uOpacity;
  varying vec3 vWorld;
  varying vec3 vNormalW;
  void main() {
    vec3 view = normalize(cameraPosition - vWorld);
    float ndv = abs(dot(normalize(vNormalW), view));
    float wrap = pow(clamp(1.0 - ndv, 0.0, 1.0), 1.35);
    vec3 col = uColor * (0.7 + wrap * 1.1);
    gl_FragColor = vec4(col, (0.07 + wrap * 0.2) * uOpacity);
  }
`;

const FACE_VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

/** Radius grows; tube and energy fall like a real expanding wave. */
export function rippleEnvelope(life) {
  const t = THREE.MathUtils.clamp(life, 0, 1);
  const k = 1 - (1 - t) * (1 - t);
  const radius = 0.3 + k * 3.1;
  const tube = THREE.MathUtils.lerp(1, 0.16, k);
  const worldWidth = 0.028 * tube;
  const opacity = Math.pow(1 - t, 1.35) / Math.sqrt(1 + k * 2.4);
  return { k, radius, tube, worldWidth, opacity };
}

function nodeBase(mesh) {
  return mesh.userData.baseColor || mesh.material.color;
}

function markEdges(graph, ev, on) {
  ev?.edges?.forEach(([from, to]) => {
    const edge = graph.edgeLinesByKey.get(`${from}>${to}`);
    if (edge) edge.userData.igniting = on;
  });
}

function additiveMaterial(vert, frag, uniforms) {
  return new THREE.ShaderMaterial({
    uniforms,
    vertexShader: vert,
    fragmentShader: frag,
    transparent: true,
    depthWrite: false,
    depthTest: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
    fog: false,
    side: THREE.DoubleSide,
  });
}

function createRibbon(kind) {
  const verts = (SEGS + 1) * 2;
  const pos = new Float32Array(verts * 3);
  const along = new Float32Array(verts);
  const side = new Float32Array(verts);
  const cols = new Float32Array(verts * 3);
  const index = new Uint16Array(SEGS * 6);
  for (let i = 0; i <= SEGS; i += 1) {
    side[i * 2] = -1;
    side[i * 2 + 1] = 1;
    if (i < SEGS) {
      const a = i * 2;
      const o = i * 6;
      index[o] = a;
      index[o + 1] = a + 1;
      index[o + 2] = a + 2;
      index[o + 3] = a + 1;
      index[o + 4] = a + 3;
      index[o + 5] = a + 2;
    }
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geom.setAttribute('aAlong', new THREE.BufferAttribute(along, 1));
  geom.setAttribute('aSide', new THREE.BufferAttribute(side, 1));
  geom.setAttribute('aColor', new THREE.BufferAttribute(cols, 3));
  geom.setIndex(new THREE.BufferAttribute(index, 1));
  const spec = {
    core: { frag: BEAM_CORE_FRAG, order: 6, half: 0.008 },
    sheath: { frag: BEAM_SHEATH_FRAG, order: 5, half: 0.026 },
  }[kind];
  const mesh = new THREE.Mesh(geom, additiveMaterial(
    BEAM_VERT,
    spec.frag,
    { uHead: { value: 0 }, uFade: { value: 1 } },
  ));
  mesh.frustumCulled = false;
  mesh.renderOrder = spec.order;
  mesh.userData = {
    isBeam: kind === 'core',
    isSheath: kind === 'sheath',
    head: new THREE.Vector3(),
    half: spec.half,
  };
  return mesh;
}

function createFace(frag, order) {
  const mesh = new THREE.Mesh(HEAD_PLANE, additiveMaterial(
    FACE_VERT,
    frag,
    { uOpacity: { value: 0 }, uColor: { value: VOID.clone() } },
  ));
  mesh.frustumCulled = false;
  mesh.renderOrder = order;
  mesh.userData.sharedGeom = true;
  return mesh;
}

function createHead() {
  const core = createFace(HEAD_CORE_FRAG, 8);
  const halo = createFace(HEAD_HALO_FRAG, 7);
  const group = new THREE.Group();
  group.add(halo, core);
  group.visible = false;
  group.userData = { isHead: true, core, halo };
  return group;
}

function createRingLayer(geom, frag, order) {
  const mesh = new THREE.Mesh(geom, additiveMaterial(RING_VERT, frag, {
    uColor: { value: VOID.clone() },
    uOpacity: { value: 0 },
    uRadius: { value: 0.3 },
    uTube: { value: 1 },
  }));
  mesh.frustumCulled = false;
  mesh.renderOrder = order;
  mesh.userData.sharedGeom = true;
  return mesh;
}

function createRing() {
  const core = createRingLayer(CORE_TORUS, RING_CORE_FRAG, 9);
  const sheath = createRingLayer(SHEATH_TORUS, RING_SHEATH_FRAG, 8);
  const group = new THREE.Group();
  group.add(sheath, core);
  group.visible = false;
  group.frustumCulled = false;
  group.userData = { core, sheath };
  return group;
}

function pathTint(nebula, x, y, t, fromColor, toColor, out) {
  if (fromColor && toColor) _fallback.copy(fromColor).lerp(toColor, THREE.MathUtils.clamp(t, 0, 1));
  else if (fromColor) _fallback.copy(fromColor);
  else if (toColor) _fallback.copy(toColor);
  else _fallback.copy(VOID);
  sampleCloudTint(nebula, x, y, out, _fallback);
  return liftSparkTint(out, out);
}

function writeRibbon(mesh, curve, head, fade, nebula, fromColor, toColor) {
  const pos = mesh.geometry.attributes.position;
  const along = mesh.geometry.attributes.aAlong;
  const cols = mesh.geometry.attributes.aColor;
  let env = 1;
  if (head < 0) env = 0;
  else if (head > 1) env = 1 - THREE.MathUtils.smoothstep(head, 1, 1.22);
  const half = mesh.userData.half * fade * Math.max(env, 0.2);
  for (let i = 0; i <= SEGS; i += 1) {
    const t = i / SEGS;
    curve.getPoint(t, _p);
    curve.getTangent(t, _tan);
    if (_tan.lengthSq() < 1e-10) _tan.set(1, 0, 0);
    else _tan.normalize();
    _side.set(-_tan.y, _tan.x, 0);
    if (_side.lengthSq() < 1e-10) _side.set(0, 1, 0);
    else _side.normalize();
    pos.setXYZ(i * 2, _p.x - _side.x * half, _p.y - _side.y * half, _p.z);
    pos.setXYZ(i * 2 + 1, _p.x + _side.x * half, _p.y + _side.y * half, _p.z);
    along.setX(i * 2, t);
    along.setX(i * 2 + 1, t);
    pathTint(nebula, _p.x, _p.y, t, fromColor, toColor, _tint);
    cols.setXYZ(i * 2, _tint.r, _tint.g, _tint.b);
    cols.setXYZ(i * 2 + 1, _tint.r, _tint.g, _tint.b);
  }
  pos.needsUpdate = true;
  along.needsUpdate = true;
  cols.needsUpdate = true;
  mesh.material.uniforms.uHead.value = head;
  mesh.material.uniforms.uFade.value = fade * env;
  curve.getPoint(THREE.MathUtils.clamp(head, 0, 1), mesh.userData.head);
}

function disposeFx(graph, obj) {
  if (!obj) return;
  graph.nodeGroup.remove(obj);
  obj.traverse((child) => {
    if (child.geometry && !child.userData.sharedGeom) child.geometry.dispose();
    if (child.material && child !== obj) child.material.dispose();
  });
  if (obj.geometry && !obj.userData.sharedGeom) obj.geometry.dispose();
  obj.material?.dispose?.();
}

function paintRing(layer, color, radius, tube, opacity) {
  const u = layer.material.uniforms;
  u.uColor.value.copy(color);
  u.uRadius.value = radius;
  u.uTube.value = tube;
  u.uOpacity.value = opacity;
}

/** Layered energy: plane-aligned needle, volume sheath, 3D ripple. Does not recolour the hairline. */
export function createIgnitionEffects(graph) {
  let event = null;
  let startedAt = 0;
  const beams = [];
  const sheaths = [];
  const heads = [];
  const rings = [];

  function clear() {
    markEdges(graph, event, false);
    [...beams, ...sheaths, ...heads, ...rings].forEach((obj) => disposeFx(graph, obj));
    beams.length = 0;
    sheaths.length = 0;
    heads.length = 0;
    rings.length = 0;
    event = null;
  }

  function fire(next, now, reducedMotion) {
    clear();
    event = next;
    startedAt = now;
    markEdges(graph, next, true);
    if (reducedMotion) return;

    next.edges.forEach(() => {
      const sheath = createRibbon('sheath');
      const beam = createRibbon('core');
      const head = createHead();
      graph.nodeGroup.add(sheath);
      graph.nodeGroup.add(beam);
      graph.nodeGroup.add(head);
      sheaths.push(sheath);
      beams.push(beam);
      heads.push(head);
    });
    [...next.sourceIds, ...next.targetIds].forEach((id) => {
      const ring = createRing();
      ring.userData.id = id;
      ring.userData.isSource = next.sourceIds.includes(id);
      graph.nodeGroup.add(ring);
      rings.push(ring);
    });
  }

  function paintNode(mesh, { source, travel, fade, age }) {
    const base = nodeBase(mesh);
    _paint.copy(base);
    if (source) {
      const lift = Math.exp(-age / 480) * 0.62 * fade;
      _paint.lerp(WHITE, lift);
      mesh.material.color.copy(_paint);
      if (mesh.material.emissive) {
        mesh.material.emissive.copy(_paint);
        mesh.material.emissiveIntensity = 0.55 + lift * 2.1;
      }
      return;
    }
    if (travel < 1) {
      _paint.multiplyScalar(0.64);
      mesh.material.color.copy(_paint);
      mesh.material.opacity = Math.min(mesh.material.opacity, 0.58);
      if (mesh.material.emissive) {
        mesh.material.emissive.copy(_paint);
        mesh.material.emissiveIntensity = 0.32;
      }
      const pending = graph.haloByTaskId.get(mesh.userData.taskId)?.halo;
      if (pending) pending.material.opacity = Math.min(pending.material.opacity, 0.08);
      return;
    }
    const flash = Math.exp(-Math.max(0, age - IGNITION_DELAY_MS - IGNITION_TRAVEL_MS) / 420) * 0.55;
    _paint.lerp(WHITE, flash * fade);
    mesh.material.color.copy(_paint);
    if (mesh.material.emissive) {
      mesh.material.emissive.copy(_paint);
      mesh.material.emissiveIntensity = 0.75 + flash * 1.4 * fade;
    }
  }

  function update(now, camera, _pixelRatio, reducedMotion) {
    if (!event) return;
    const age = now - startedAt;
    if (age > IGNITION_DURATION_MS) { clear(); return; }
    const fade = 1 - THREE.MathUtils.smoothstep(age, IGNITION_DURATION_MS - 1100, IGNITION_DURATION_MS);
    const travel = reducedMotion ? 1 : (age - IGNITION_DELAY_MS) / IGNITION_TRAVEL_MS;

    event.edges.forEach(([from, to], i) => {
      const edge = graph.edgeLinesByKey.get(`${from}>${to}`);
      const beam = beams[i];
      const sheath = sheaths[i];
      const head = heads[i];
      if (!beam || !edge?.userData.curve || edge.userData.exiting) {
        if (beam) beam.visible = false;
        if (sheath) sheath.visible = false;
        if (head) head.visible = false;
        return;
      }
      const fromColor = graph.meshByTaskId.get(from)?.userData.baseColor;
      const toColor = graph.meshByTaskId.get(to)?.userData.baseColor;
      writeRibbon(beam, edge.userData.curve, travel, fade, graph.nebula, fromColor, toColor);
      if (sheath) writeRibbon(sheath, edge.userData.curve, travel, fade, graph.nebula, fromColor, toColor);
      const beamOn = !reducedMotion && travel > -0.04 && travel < 1.28;
      beam.visible = beamOn;
      if (sheath) sheath.visible = beamOn;
      if (head) {
        const on = !reducedMotion && travel > 0 && travel < 1.06;
        head.visible = on;
        if (on) {
          head.position.copy(beam.userData.head);
          head.quaternion.copy(camera.quaternion);
          pathTint(graph.nebula, head.position.x, head.position.y, travel, fromColor, toColor, _tint);
          const opacity = fade * (travel > 1 ? 1 - (travel - 1) * 12 : 1);
          head.userData.core.scale.setScalar(0.048);
          head.userData.halo.scale.setScalar(0.2);
          head.userData.core.material.uniforms.uColor.value.copy(_tint);
          head.userData.halo.material.uniforms.uColor.value.copy(_tint);
          head.userData.core.material.uniforms.uOpacity.value = opacity;
          head.userData.halo.material.uniforms.uOpacity.value = opacity;
        }
      }
    });

    [...event.sourceIds, ...event.targetIds].forEach((id) => {
      const mesh = graph.meshByTaskId.get(id);
      if (!mesh?.visible || mesh.userData.exiting) return;
      paintNode(mesh, { source: event.sourceIds.includes(id), travel, fade, age });
      mesh.userData.ignitionUntil = startedAt + IGNITION_DURATION_MS;
    });

    rings.forEach((ring) => {
      const mesh = graph.meshByTaskId.get(ring.userData.id);
      const start = ring.userData.isSource ? 0 : IGNITION_DELAY_MS + IGNITION_TRAVEL_MS;
      const life = (age - start) / RING_MS;
      const on = !reducedMotion && !!mesh?.visible && life >= 0 && life <= 1;
      ring.visible = on;
      if (!on) return;
      const wave = rippleEnvelope(life);
      ring.position.copy(mesh.position);
      ring.position.z += 0.05;
      ring.quaternion.identity();
      const layout = mesh.userData.layoutScale > 0 ? mesh.userData.layoutScale : Math.max(mesh.scale.x, 1);
      const radius = wave.radius * layout;
      const base = nodeBase(mesh);
      pathTint(graph.nebula, mesh.position.x, mesh.position.y, ring.userData.isSource ? 0 : 1, base, base, _tint);
      paintRing(ring.userData.core, _tint, radius, wave.tube, wave.opacity);
      paintRing(ring.userData.sheath, _tint, radius, wave.tube * 1.2, wave.opacity * 0.85);
    });
  }

  return { fire, update, clear, getEvent: () => event };
}